import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, User, ArrowRight, Loader } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API_BASE } from '../lib/api';

const API = API_BASE;

const Blog = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const lang = i18n.language;

  useEffect(() => {
    axios.get(`${API}/blog`)
      .then(res => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <>
      <Navbar />
      <div data-testid="blog-page" className="min-h-screen pt-16 bg-[#F5F5F5] dark:bg-[#0F1C2E]">
        {/* Header */}
        <div className="py-20 bg-white dark:bg-[#1A2639] text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-[#7D468C]/8 pointer-events-none" style={{ transform: 'rotate(45deg)', transformOrigin: 'top right' }} />
          <div className="relative max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-3 h-3 bg-[#4EBFBF]" style={{ transform: 'rotate(45deg)' }} />
              <span className="text-sm font-semibold text-[#4EBFBF] tracking-widest uppercase font-poppins">
                {t('blog.subtitle')}
              </span>
              <div className="w-3 h-3 bg-[#4EBFBF]" style={{ transform: 'rotate(45deg)' }} />
            </div>
            <h1 className="font-poppins font-black text-5xl text-[#233559] dark:text-white">{t('blog.title')}</h1>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader size={24} className="animate-spin text-[#7D468C]" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-24" data-testid="blog-empty">
              <div className="w-20 h-20 border-2 border-[#4EBFBF]/20 mx-auto mb-6" style={{ transform: 'rotate(45deg)' }} />
              <p className="text-[#233559]/40 dark:text-white/35 font-source-sans">{t('blog.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map(post => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.id}`}
                  className="group bg-white dark:bg-[#1A2639] overflow-hidden border border-transparent hover:border-[#4EBFBF]/30 transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300 flex flex-col"
                >
                  {/* Featured image */}
                  <div className="aspect-video bg-[#4EBFBF]/8 overflow-hidden relative flex-shrink-0">
                    {post.featured_image ? (
                      <img src={post.featured_image} alt={lang === 'en' ? post.title_en : post.title_es} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-16 h-16 bg-[#4EBFBF]/20" style={{ transform: 'rotate(45deg)' }} />
                      </div>
                    )}
                    {post.category && (
                      <div className="absolute top-3 left-3 bg-[#4EBFBF] text-white text-xs px-3 py-1 font-poppins font-semibold uppercase tracking-wide">
                        {post.category}
                      </div>
                    )}
                  </div>

                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-[#233559]/40 dark:text-white/35 font-source-sans mb-3">
                      <span className="flex items-center gap-1"><User size={11} /> {post.author}</span>
                      <span className="flex items-center gap-1"><Calendar size={11} /> {formatDate(post.created_at)}</span>
                    </div>
                    <h3 className="font-poppins font-bold text-lg text-[#233559] dark:text-white mb-2 line-clamp-2 flex-1">
                      {lang === 'en' ? post.title_en : post.title_es}
                    </h3>
                    <p className="text-sm text-[#233559]/55 dark:text-white/50 mb-4 font-source-sans line-clamp-2">
                      {lang === 'en' ? post.excerpt_en : post.excerpt_es}
                    </p>
                    <span className="text-xs font-poppins font-bold text-[#4EBFBF] flex items-center gap-1.5 uppercase tracking-wide group-hover:gap-3 transition-[gap]">
                      {t('blog.read_more')} <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Blog;
